import React, { FC, useState, useCallback, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useSearchParams } from 'react-router-dom';
import {
  SkipPermissionsResponse,
  GetGoogleLinkedAccountsResponse,
  GoogleLinkedAccount,
} from '@dreamai/types';
import { ReactComponent as GoogleLogo } from '../../../icons/google-logo.svg';
import { ReactComponent as ArrowCircleRight } from '../../../icons/arrow-right-circle.svg';
import api from '../../../api';
import { OnboardingStepProps } from '../OnboardingFlow';
import './connect-accounts-step.scss';

const MODAL_DURATION_MS = 15_000;
const PILL_LABELS = ['About you', 'Your work', 'People you know'];

const CloudMoonIcon: FC = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='43'
    height='43'
    viewBox='0 0 43 43'
    fill='none'
  >
    <path
      d='M30.5 23.1729C35.6536 23.1729 39.9794 19.6288 41.1732 14.8449C40.3174 15.0586 39.422 15.172 38.5 15.172C32.4249 15.172 27.5 10.2471 27.5 4.17201C27.5 3.25048 27.6133 2.35542 27.8268 1.5C23.0435 2.69421 19.5 7.01975 19.5 12.1729C19.5 13.9984 19.9446 15.7199 20.7315 17.2353M9.5 41.1729C5.08172 41.1729 1.5 37.5911 1.5 33.1729C1.5 29.2171 4.37102 25.932 8.14296 25.2875C9.75751 20.5669 14.2324 17.1729 19.5 17.1729C24.0764 17.1729 28.0545 19.7346 30.0784 23.5024C30.8489 23.2877 31.661 23.1729 32.5 23.1729C37.4706 23.1729 41.5 27.2023 41.5 32.1729C41.5 37.1434 37.4706 41.1729 32.5 41.1729C24.8333 41.1729 17.1667 41.1729 9.5 41.1729Z'
      stroke='url(#paint0_linear_cloud_moon)'
      strokeWidth='3'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <defs>
      <linearGradient
        id='paint0_linear_cloud_moon'
        x1='21.5'
        y1='1.5'
        x2='21.5'
        y2='41.1729'
        gradientUnits='userSpaceOnUse'
      >
        <stop stopColor='#489FD9' />
        <stop offset='1' stopColor='#EE4266' />
      </linearGradient>
    </defs>
  </svg>
);

const ConnectAccountsStep: FC<OnboardingStepProps> = ({ refreshStatus }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [searchParams] = useSearchParams();
  const [accounts, setAccounts] = useState<GoogleLinkedAccount[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [activePill, setActivePill] = useState(0);
  const modalTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pillIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchAccounts = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<GetGoogleLinkedAccountsResponse>(
        '/google-link',
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setAccounts(res.data.linkedAccounts);
    } catch (err) {
      console.error('Error fetching linked accounts:', err);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  useEffect(() => {
    const status = searchParams.get('status');
    if (status === 'success') {
      fetchAccounts();
    }
  }, []);

  // Clean up timers on unmount
  useEffect(() => {
    return () => {
      if (modalTimerRef.current) clearTimeout(modalTimerRef.current);
      if (pillIntervalRef.current) clearInterval(pillIntervalRef.current);
    };
  }, []);

  const handleComplete = useCallback(async () => {
    setSubmitting(true);
    try {
      const token = await getAccessTokenSilently();
      await api.post<SkipPermissionsResponse>(
        '/onboarding/skip-permissions',
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Show the "Making memories" modal for 15 seconds
      setShowModal(true);
      setActivePill(0);

      // Cycle the active pill highlight
      let pillIdx = 0;
      pillIntervalRef.current = setInterval(() => {
        pillIdx = (pillIdx + 1) % PILL_LABELS.length;
        setActivePill(pillIdx);
      }, 1200);

      // After 15 seconds, dismiss the modal and advance
      modalTimerRef.current = setTimeout(async () => {
        if (pillIntervalRef.current) clearInterval(pillIntervalRef.current);
        setShowModal(false);
        await refreshStatus();
      }, MODAL_DURATION_MS);
    } catch (err) {
      console.error('Error completing permissions step:', err);
      setSubmitting(false);
    }
  }, [getAccessTokenSilently, refreshStatus]);

  const handleConnectAccount = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const params = new URLSearchParams({
        scope: 'email calendar workspace',
        page_id: 'onboarding',
      });
      const { data } = await api.get<{ url: string }>(
        `/google-link/initiate-google-oauth?${params}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      window.location.href = data.url;
    } catch (err) {
      console.error('Error starting Google connect flow:', err);
    }
  }, [getAccessTokenSilently]);

  const handleRemoveAccount = useCallback(
    async (oauthId: number) => {
      try {
        const token = await getAccessTokenSilently();
        await api.delete(`/google-link/${oauthId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAccounts((prev) => prev.filter((a) => a.id !== oauthId));
      } catch (err: any) {
        const errorData = err?.response?.data;
        if (errorData?.error === 'Account in use') {
          alert(
            `This account is in use by ${errorData.profiles.length} profile(s) and cannot be removed.`,
          );
        } else {
          console.error('Error removing account:', err);
        }
      }
    },
    [getAccessTokenSilently],
  );

  const hasAccounts = accounts.length > 0;

  return (
    <div className='onboarding-step connect-accounts-step'>
      <div className='connect-layout'>
        {/* Left panel — copy + Next button */}
        <div className='connect-left'>
          <h1 className='connect-title'>Let's make memories</h1>
          <div className='connect-description'>
            <p>
              Being a great secretary means learning how to help you. Connecting
              your active email and calendar accounts helps me do this.
            </p>
            <p>
              I'll learn about you and remember, so you don't have to tell me
              the same details over and over.
            </p>
            <p>I won't send any messages from the accounts you connect.</p>
          </div>
          {hasAccounts && (
            <div className='connect-actions'>
              <button
                className='step-button'
                onClick={handleComplete}
                disabled={submitting}
              >
                Next
                <ArrowCircleRight />
              </button>
            </div>
          )}
        </div>

        {/* Right panel — connect card */}
        <div className='connect-right'>
          {accounts.map((account) => (
            <div key={account.id} className='connected-account'>
              <span className='account-email'>{account.email}</span>
              <button
                className='remove-account-button'
                onClick={() => handleRemoveAccount(account.id)}
                aria-label={`Remove ${account.email}`}
              >
                <svg
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                >
                  <circle cx='12' cy='12' r='9' />
                  <path d='M8 12h8' />
                </svg>
              </button>
            </div>
          ))}

          <button
            className='connect-account-button'
            onClick={handleConnectAccount}
          >
            <GoogleLogo className='google-logo' />
            <span>Connect your account</span>
          </button>

          <p className='connect-card-disclaimer'>
            We only support Gmail or Google Workspace accounts right now.
          </p>
        </div>
      </div>

      {/* "Making memories" overlay modal */}
      {showModal && (
        <div className='memories-overlay'>
          <div className='memories-modal'>
            <CloudMoonIcon />
            <h2 className='memories-title'>Making memories</h2>
            <div className='memories-pills'>
              {PILL_LABELS.map((label, i) => (
                <span
                  key={label}
                  className={`memories-pill${i === activePill ? ' active' : i === (activePill - 1 + PILL_LABELS.length) % PILL_LABELS.length ? ' fading' : ''}`}
                >
                  {label}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectAccountsStep;
